package dev.lingvo.site.api

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException

/**
 * Which sign-in methods this deployment offers, fetched once and shared.
 *
 * **Why this is not a `LaunchedEffect` inside the button.** It was, and it had two faults. The first
 * is the one this codebase keeps relearning: Compose HTML runs effects off `requestAnimationFrame`,
 * which a browser suspends for a tab it is not painting, so the fetch never happened and the button
 * never appeared. The second is milder but visible to everyone — every auth page that drew the
 * button started its own request, and the button appeared a round trip late, shifting the form
 * underneath it.
 *
 * Fetching once at application start fixes both. By the time anybody navigates to a sign-in page the
 * answer is usually already here, and a cold load into `/login` shifts nothing because the button's
 * space is reserved while the answer is unknown.
 *
 * Defaults to **unknown**, never to "available". Guessing wrong in that direction offers a button
 * that leads to a 404 on a deployment without OAuth credentials, and a user cannot tell a broken
 * product from a broken account.
 */
object AuthProviders {

    /** Null until the answer arrives. Callers reserve space rather than assuming either way. */
    var googleAvailable: Boolean? by mutableStateOf(null)
        private set

    /**
     * Paddle's client-side token, or null when payments are unavailable *or* the answer has not
     * arrived yet. The checkout page distinguishes the two by waiting rather than by guessing.
     *
     * Public by design: it can open a checkout and nothing else.
     */
    var paddleClientToken: String? by mutableStateOf(null)
        private set

    /** "sandbox" or "production". Paddle.js must be told, and it has to match the token. */
    var paddleEnvironment: String? by mutableStateOf(null)
        private set

    private var started = false

    /**
     * Starts the one fetch. Safe to call repeatedly — later calls are ignored.
     *
     * A failure leaves the answer as `false` rather than retrying: a sign-in page that cannot reach
     * the API has larger problems than a missing button, and a retry loop behind a button nobody can
     * see is not worth the requests.
     */
    suspend fun load() {
        if (started) return
        started = true
        try {
            val providers = ApiClient.authProviders()
            googleAvailable = providers.google
            paddleClientToken = providers.paddleClientToken
            paddleEnvironment = providers.paddleEnvironment
        } catch (cancelled: CancellationException) {
            started = false
            throw cancelled
        } catch (_: Throwable) {
            googleAvailable = false
        }
    }
}
